import { useCallback, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";

import { chooseFalFund, getFalFunds } from "../connectors/connectors";
import {
  FalElectionError,
  FalFund,
  FalFundsInfo,
  mockFalFunds,
} from "../utils/falFunds";
import { UUID_PATTERN } from "../utils/partnerConsent";
import { isDev } from "../app/utils";

export type FalFundSelectionScreen =
  | "LOADING"
  | "SELECTION"
  | "SUCCESS"
  | "ALREADY_CHOSEN"
  | "NOT_REGISTERED"
  | "INVALID_LINK"
  | "ERROR";

const MOCKS_ENABLED = import.meta.env.VITE_ENVIRONMENT === "dev";

const mockScreens: Record<string, FalFundSelectionScreen> = {
  selection: "SELECTION",
  success: "SUCCESS",
  chosen: "ALREADY_CHOSEN",
  unregistered: "NOT_REGISTERED",
  invalid: "INVALID_LINK",
  error: "ERROR",
};

const RAZON_SOCIAL_PARAM = "razon_social=";

// Postmark doesn't URL-encode template variables, so a razón social with "&"
// would split the query: the link puts it last and it's read raw to the end.
const readRazonSocial = (search: string) => {
  const start = search.indexOf(RAZON_SOCIAL_PARAM);
  if (start === -1) return "";
  const raw = search.slice(start + RAZON_SOCIAL_PARAM.length);
  try {
    return decodeURIComponent(raw.replace(/\+/g, " ")).trim();
  } catch {
    return raw.trim();
  }
};

class FalElectionFailure extends Error {
  constructor(public response: FalElectionError) {
    super(`${response.codigo}: ${response.mensaje}`);
  }
}

const unwrap = <T extends { ok: true }>(data: T | FalElectionError) => {
  if (!data?.ok) throw new FalElectionFailure(data as FalElectionError);
  return data;
};

const useFalFundSelection = () => {
  const [searchParams] = useSearchParams();
  const { search } = useLocation();

  const mockScreen = MOCKS_ENABLED
    ? mockScreens[searchParams.get("mock") ?? ""]
    : undefined;
  const requestUuid = searchParams.get("uuid")?.trim() ?? "";
  const shareholder =
    searchParams.get("cuotapartista")?.trim() || (mockScreen ? "322" : "");
  const holder = readRazonSocial(search) || (mockScreen ? "Empresa S.A." : "");

  const [screen, setScreen] = useState<FalFundSelectionScreen>(
    mockScreen ?? "LOADING",
  );
  const [funds, setFunds] = useState<FalFundsInfo | null>(
    mockScreen ? mockFalFunds : null,
  );
  const [pendingFund, setPendingFund] = useState<FalFund | null>(null);
  const [chosenFund, setChosenFund] = useState(
    mockScreen ? mockFalFunds.fondos[1].nombre : "",
  );
  const [chosenAt, setChosenAt] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fail = useCallback((error: unknown, action: string) => {
    const response =
      error instanceof FalElectionFailure ? error.response : undefined;

    if (response?.codigo === "YA_ELEGIDO") {
      setChosenFund(response.falElegido ?? "");
      setChosenAt(response.fechaAceptado ?? "");
      setScreen("ALREADY_CHOSEN");
      return;
    }
    if (response?.codigo === "NO_REGISTRADO") {
      setScreen("NOT_REGISTERED");
      return;
    }
    if (response?.codigo === "DATOS_INVALIDOS") {
      setScreen("INVALID_LINK");
      return;
    }

    Sentry.captureException(error, {
      extra: { action },
      tags: { flow: "fal-fund-selection" },
    });
    if (isDev) console.warn(`[fal-fund-selection] ${action} failed:`, error);
    setScreen("ERROR");
  }, []);

  const load = useCallback(async () => {
    if (!UUID_PATTERN.test(requestUuid) || !/^\d+$/.test(shareholder)) {
      setScreen("INVALID_LINK");
      return;
    }

    setScreen("LOADING");
    try {
      const { data } = await getFalFunds();
      setFunds(unwrap(data));
      setScreen("SELECTION");
    } catch (error) {
      fail(error, "fondos");
    }
  }, [requestUuid, shareholder, fail]);

  useEffect(() => {
    if (!mockScreen) load();
  }, [mockScreen, load]);

  const confirm = async () => {
    if (!pendingFund) return;

    if (mockScreen) {
      setChosenFund(pendingFund.nombre);
      setPendingFund(null);
      setScreen("SUCCESS");
      return;
    }

    setIsSubmitting(true);
    try {
      const { data } = await chooseFalFund({
        uuid: requestUuid,
        cuotapartista: shareholder,
        fondo: pendingFund.id,
      });
      unwrap(data);
      setChosenFund(pendingFund.nombre);
      setScreen("SUCCESS");
    } catch (error) {
      fail(error, "elegir");
    } finally {
      setIsSubmitting(false);
      setPendingFund(null);
    }
  };

  return {
    screen,
    funds,
    holder,
    pendingFund,
    chosenFund,
    chosenAt,
    isSubmitting,
    choose: setPendingFund,
    cancel: () => setPendingFund(null),
    confirm,
    // A failed choice keeps the funds already loaded: back to the cards.
    retry: funds ? () => setScreen("SELECTION") : load,
  };
};

export default useFalFundSelection;
